<script lang="ts" setup>
import { useResponsive } from "@core/composables"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import { useVModel } from "@vueuse/core"
import moment from "moment"
import { useForm } from "vee-validate"
import { ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import * as yup from "yup"
import { storeToRefs } from "pinia"

const { isMobile } = useResponsive()

type Props = {
    visible: boolean
}
const emit = defineEmits(["update:visible"])
const props = defineProps<Props>()

const emConsoCompareStore = useEmConsoCompareStore()

const { hasErrorExportRT, isFetchingExportRT, errorExportRT } =
    storeToRefs(emConsoCompareStore)

// ============================================
// Traduction
// ============================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Export des données du Référentiel Technique",
            quit: "Quitter",
            question: "Spécifications de l'export",
            period: {
                label: "Période",
                start_date: {
                    label: "Date de début",
                    placeholder: "--/--/----",
                },
                end_date: {
                    label: "Date de fin",
                    placeholder: "--/--/----",
                },
                error: "La date de début doit être antérieure à la date de fin.",
            },
            checkbox: {
                title: "Si des mois sont incomplets sur la période sélectionnée :",
                aggregate:
                    "Calculer les données au niveau du mois avec les données disponibles",
                not_aggregate: "Laisser la cellule correspondante sera vide",
                tootltip:
                    'L\'onglet "Période" sera par conséquent vide en cas de trous de données sur la période demandée.',
            },
            description:
                "Le Référentiel Technique est la base de données officielle qui centralise toutes les informations relatives au patrimoine immobilier de l’État et de ses opérateurs. L’export que vous réalisez ici vous permet de récupérer les données d'énergie structurées vous permettant de compléter ce référentiel",
            submit: "Générer l'export",
        },
    },
})

// ============================================
// Gestion de la visibilité de la modale
// ============================================

const visible = useVModel(props, "visible", emit)

// ============================================
// Gestion du formulaire
// ============================================

const schema = yup.object({
    aggregate: yup.boolean().required().default(true),
    date_from: yup
        .date()
        .required()
        .test(
            "start-before-end",
            "La date de début doit être antérieure à la date de fin.",
            function (value) {
                const { date_to } = this.parent
                if (!value || !date_to) return true

                if (!value || !date_to) return false
                return value.getTime() < date_to.getTime()
            }
        ),

    date_to: yup
        .date()
        .required()
        .test(
            "end-after-start",
            "La date de fin doit être postérieure à la date de début.",
            function (value) {
                const { date_from } = this.parent
                if (!value || !date_from) return true

                if (!date_from || !value) return false
                return value.getTime() > date_from.getTime()
            }
        )
        .test(
            "end-not-after-today",
            "La date de fin ne peut pas dépasser la date d'aujourd'hui.",
            function (value) {
                if (!value) return true

                const today = new Date()

                today.setHours(0, 0, 0, 0)

                value.setHours(0, 0, 0, 0)

                return value.getTime() <= today.getTime()
            }
        ),
})

const { defineField, handleSubmit, errors } = useForm({
    validationSchema: schema,
    initialValues: {
        aggregate: true,
        date_from: moment().subtract(3, "month").set("h", 0).toDate(),
        date_to: moment().set("h", 0).subtract(1, "day").toDate(),
    } as {
        date_from: Date
        date_to: Date
        aggregate: boolean
    },
})

const [dateFrom] = defineField("date_from")
const [dateTo] = defineField("date_to")
const [aggregate] = defineField("aggregate")

const isFecthingTask = ref(false)

/**
 * Quand l'utilisateur clique sur exporter.
 * Appel l'export et ferme la modale esuite
 */
const onSubmit = handleSubmit((values) => {
    isFecthingTask.value = true
    // Lance la tâche de l'export
    emConsoCompareStore.fetchExportRT({
        aggregate: values.aggregate,
        date_from: moment(dateFrom.value).format("YYYY-MM-DD"),
        date_to: moment(dateTo.value).format("YYYY-MM-DD"),
        callback: () => {
            visible.value = false
            isFecthingTask.value = false
        },
    })
})

watch(
    () => isFetchingExportRT.value,
    (value) => {
        if (!value) isFecthingTask.value = value
    }
)
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        :position="isMobile ? 'center' : 'top'"
        modal
        class="dashboard-modal-export"
        :pt="{
            footer: 'dashboard-model-select-building-footer',
        }"
        :style="{ maxWidth: '40rem', width: '100%' }"
        :breakpoints="{ '1199px': '90vw' }"
    >
        <template #header>
            <h2 class="text-xl font-bold dashboard-modal-export__title">
                {{ t("title") }}
            </h2>
        </template>
        <div class="dashboard-modal-export__content">
            <p class="text-xs">{{ t("description") }}</p>
            <!-- Choix de la période lorsque le type est selectionné -->
            <div class="dashboard-modal-export__content">
                <h4 class="font-bold">{{ t("period.label") }}</h4>
                <div class="dashboard-modal-export__dates-wrapper">
                    <!-- Date de début -->
                    <BaseInputWrapper
                        input-id="date_from"
                        :label="t('period.start_date.label')"
                        :error="errors.date_from"
                    >
                        <BaseDatePicker
                            input-id="date_from"
                            v-model="dateFrom"
                            date-format="dd/mm/yy"
                            v-mask="'99/99/9999'"
                            :invalid="!!errors.date_from"
                            :max-date="moment().subtract(1, 'day').toDate()"
                        ></BaseDatePicker>
                    </BaseInputWrapper>
                    <!-- Date de fin -->
                    <BaseInputWrapper
                        input-id="date_to"
                        :label="t('period.end_date.label')"
                        :error="errors.date_to"
                    >
                        <BaseDatePicker
                            input-id="date_to"
                            v-model="dateTo"
                            date-format="dd/mm/yy"
                            v-mask="'99/99/9999'"
                            :invalid="!!errors.date_to"
                            :max-date="moment().subtract(1, 'day').toDate()"
                        ></BaseDatePicker>
                    </BaseInputWrapper>
                </div>
            </div>
            <!-- Choix de la gestion des trous de données -->
            <div class="dashboard-modal-export__content">
                <h4 class="font-bold">{{ t("checkbox.title") }}</h4>
                <div class="flex items-center gap-2">
                    <BaseRadioButton
                        v-model="aggregate"
                        input-id="aggregate"
                        :value="true"
                        name="export-all-data-check"
                    />
                    <label for="aggregate">{{ t("checkbox.aggregate") }}</label>
                </div>
                <div class="flex items-center gap-2">
                    <BaseRadioButton
                        v-model="aggregate"
                        input-id="not_aggregate"
                        :value="false"
                        name="export-all-data-check"
                    />
                    <label for="not_aggregate"
                        >{{ t("checkbox.not_aggregate")
                        }}<i
                            class="ml-2 pi pi-info-circle"
                            v-tooltip.left="{
                                value: t('checkbox.tootltip'),
                                pt: {
                                    text: 'text-sm',
                                },
                            }"
                        ></i
                    ></label>
                </div>
            </div>
        </div>
        <template #footer>
            <div class="flex flex-col gap-4 w-full">
                <div class="flex justify-between w-full">
                    <BaseButton
                        :label="t('quit')"
                        text
                        @click="visible = false"
                    ></BaseButton>
                    <BaseButton
                        :label="t('submit')"
                        @click="onSubmit"
                        :loading="isFecthingTask"
                    ></BaseButton>
                </div>
                <BaseAppMessage v-if="hasErrorExportRT" severity="error">
                    {{ errorExportRT }}
                </BaseAppMessage>
            </div>
        </template>
    </BaseDialog>
</template>

<style scoped>
@import "@styles/breakpoints.css";

.dashboard-modal-export {
    .dashboard-modal-export__title {
        text-align: center;
        width: 100%;
    }
    .dashboard-modal-export__content {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .dashboard-modal-export__dates-wrapper {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 1rem;
        width: 100%;
    }
}
.dashboard-modal-export__option-group-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}
</style>

<style>
.dashboard-model-select-building-footer {
    justify-content: space-between !important;
}
</style>
